// Navigation + highlight for product-help "Show me" buttons.
//
// A help nav payload ({tab, anchor}) comes from the backend's
// get_product_help tool via the SSE `done` event. Clicking "Show me"
// switches the main tab and pulse-highlights the element carrying the
// matching `data-help-id` attribute once it mounts. Anchors live on
// the real elements, so a moved/renamed control degrades gracefully
// (no highlight) instead of pointing users at stale directions.

const FIND_TIMEOUT_MS = 6000;
const POLL_INTERVAL_MS = 150;
const PULSE_COLOR = "rgba(109, 40, 217, 0.55)"; // violet, matches #6d28d9 scheme

// One live pulse per element. Restarting a pulse mid-flash must reuse the
// styles saved by the FIRST run — capturing "previous" styles while a pulse
// is active would save the pulse shadow itself and restore it forever,
// leaving a permanent violet border after a re-clicked "Show me" button.
const activePulses = new WeakMap();

export function pulseElement(el) {
  el.scrollIntoView({ behavior: "smooth", block: "center" });

  const existing = activePulses.get(el);
  if (existing) {
    clearInterval(existing.interval);
    clearTimeout(existing.timeout);
  }
  const original = existing
    ? existing.original
    : {
        boxShadow: el.style.boxShadow,
        transition: el.style.transition,
        borderRadius: el.style.borderRadius,
      };

  el.style.transition = "box-shadow 350ms ease-in-out";
  // Soften the pulse corners only when the element has no rounding of
  // its own (computed, not inline — Tailwind classes like rounded-full
  // set it via stylesheet and must not be stomped).
  if (window.getComputedStyle(el).borderRadius === "0px") {
    el.style.borderRadius = "6px";
  }

  let on = false;
  const toggle = () => {
    on = !on;
    el.style.boxShadow = on ? `0 0 0 4px ${PULSE_COLOR}` : "none";
  };
  toggle();
  const interval = setInterval(toggle, 700);
  const timeout = setTimeout(() => {
    clearInterval(interval);
    el.style.boxShadow = original.boxShadow;
    el.style.transition = original.transition;
    el.style.borderRadius = original.borderRadius;
    activePulses.delete(el);
  }, 4200);
  activePulses.set(el, { interval, timeout, original });
}

// Poll for an anchor element until it mounts (tab content renders
// async after a switch / a click) or the timeout passes.
function waitForAnchor(anchorId) {
  return new Promise((resolve) => {
    const deadline = Date.now() + FIND_TIMEOUT_MS;
    const timer = setInterval(() => {
      const el = document.querySelector(`[data-help-id="${anchorId}"]`);
      if (el) {
        clearInterval(timer);
        resolve(el);
      } else if (Date.now() > deadline) {
        clearInterval(timer);
        resolve(null); // graceful no-op — stale anchor degrades silently
      }
    }, POLL_INTERVAL_MS);
  });
}

// nav payload shapes (from the backend help registry):
//   { tab, anchor }                      — switch tab, pulse anchor
//   { tab, steps: [{anchor, click}] }    — switch tab, then walk the
//     steps in order: wait for each step's anchor, click it when
//     step.click is true. The LAST step's element gets the pulse, so
//     a single {anchor, click: true} step clicks into a subtab and
//     pulses it; a click step followed by a plain step drills in and
//     highlights the revealed control.
export async function navigateAndHighlight(nav, setCurrentTab) {
  if (!nav) return;
  if (nav.tab && typeof setCurrentTab === "function") {
    setCurrentTab(nav.tab);
  }
  const steps =
    Array.isArray(nav.steps) && nav.steps.length > 0
      ? nav.steps
      : nav.anchor
      ? [{ anchor: nav.anchor }]
      : [];
  let lastEl = null;
  for (const step of steps) {
    if (!step || !step.anchor) continue;
    const el = await waitForAnchor(step.anchor);
    if (!el) return; // chain broken — stop rather than click blind
    if (step.click) {
      el.click();
    }
    lastEl = el;
  }
  if (lastEl) {
    pulseElement(lastEl);
  }
}
